import CustomPagination from "../../__utilities/components/CustomPagination";
import { formatNumber } from "__utilities/functions/public";
import { useAdmin } from "_data/hooks/admin/useAdmin";
import { useEffect, useState } from "react";
import {
  DataGrid,
  GridFooterContainer,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
  useGridSelector,
  gridFilteredSortedRowIdsSelector,
  gridRowsLookupSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(100);
  const { getListUsersData } = useAdmin();
  const apiRef = useGridApiRef();

  const getUsers = async () => {
    await getListUsersData((success, response) => {
      if (success) {
        let usersData = response
          .map((u) => ({ ...u, id: uuidv4() }))
          .filter((u) => !u.isDemo);

        setUsers(usersData);
      }
    });
  };

  const licenseExpiringSoon = (date) => {
    const expirationDate = new Date(date);
    const currentDate = new Date();
    if (expirationDate > currentDate) {
      const timeDifference = expirationDate - currentDate;
      const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
      return daysLeft <= 5 && daysLeft;
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const columns = [
    {
      field: "signupDate",
      headerName: "Signup date",
      renderCell: (params) => {
        return (
          <span>
            {params.value
              ? new Date(params.value).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hourCycle: "h23",
                })
              : ""}
          </span>
        );
      },
      flex: 1,
    },
    {
      field: "provider",
      headerName: "Provider",
      renderCell: (params) => {
        switch (params.value) {
          case "MicrosoftAccount":
            return (
              <div className="flex items-center justify-center h-full">
                <img
                  className="w-4 h-4 opacity-50 content-microsoftIcon tall:h-5 tall:w-5"
                  alt=""
                />
              </div>
            );
          default:
            return null;
        }
      },
      flex: 0.5,
    },
    { field: "username", headerName: "Username", flex: 1.8 },
    { field: "displayName", headerName: "Display name", flex: 1.8 },
    { field: "licenseStatus", headerName: "License status", flex: 0.7 },
    { field: "subscriptionType", headerName: "Subscription type", flex: 0.8 },
    {
      field: "licenseExpiration",
      headerName: "License expiration",
      renderCell: (params) => {
        return (
          <span
            className={`${
              licenseExpiringSoon(params.value) && "text-[#C58B68]"
            }`}
          >
            {params.value
              ? new Date(params.value).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hourCycle: "h23",
                })
              : ""}
          </span>
        );
      },
      flex: 1,
    },
    {
      field: "uniqueExtracts",
      headerName: "Unique",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
    {
      field: "limitedExtracts",
      headerName: "Limited",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
    {
      field: "generatedExtracts",
      headerName: "Generated",
      headerAlign: "right",
      align: "right",
      flex: 0.5,
    },
  ];

  const pageCount = Math.ceil(users.length / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= pageCount) return;
    setPage(newPage);
    apiRef.current.setPage(newPage);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        <DataGrid
          apiRef={apiRef}
          className="data-grid-users text-[9px] tall:text-sm text-primaryBlue"
          columnHeaderHeight={35}
          rowHeight={30}
          slots={{
            footer: () => <CustomFooter />,
          }}
          slotProps={{
            columnMenu: {
              sx: {
                minWidth: "150px",
                "& .MuiDivider-root": {
                  marginBlock: "0px",
                },
                "& .MuiButtonBase-root": {
                  paddingInline: "5px",
                },
                "& .MuiSvgIcon-root": {
                  scale: 0.6,
                },
                "& .MuiListItemText-primary": {
                  fontSize: {
                    sm: "10px",
                    md: "10px",
                    lg: "10px",
                    xl: "13px",
                  },
                },
              },
            },
          }}
          pageSizeOptions={[100]}
          hideFooterSelectedRowCount
          pagination
          rows={users}
          columns={columns}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 1 ? "bg-[#F6F5F3]" : ""
          }
          getRowId={(row) => row.id}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#F0F3F5",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: "#003057",
            },
            "& .MuiDataGrid-cell": {
              color: "#003057",
            },
            "& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--header":
              {
                backgroundColor: "#F0F3F5",
              },
          }}
        />
      </div>

      <Box className="flex-shrink-0 mt-2">
        <CustomPagination
          page={page}
          pageCount={pageCount}
          onPageChange={handlePageChange}
        />
      </Box>
    </div>
  );
}

function CustomFooter() {
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector,
  );

  const filteredSortedRowIds = useGridSelector(
    apiRef,
    gridFilteredSortedRowIdsSelector,
  );
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const filteredRows = filteredSortedRowIds.map((id) => rowsLookup[id]);

  let totalNoDocs = filteredRows.reduce(
    (sum, user) => sum + user.uniqueExtracts,
    0,
  );
  let totalRequestedPages = filteredRows.reduce(
    (sum, user) => sum + user.limitedExtracts,
    0,
  );
  let totalPages = filteredRows.reduce(
    (sum, user) => sum + user.generatedExtracts,
    0,
  );
  let activePremiumUsersCount = filteredRows.filter(
    (user) => user?.license === "Premium" && !user?.licenseHasExpired,
  ).length;

  return (
    <GridFooterContainer
      className={`flex flex-col h800:!min-h-[30px] h801:!min-h-[40px]`}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          backgroundColor: "#F0F3F5",
        }}
      >
        {visibleColumns.map((col) => {
          let value = "";

          if (col.field === "signupDate")
            value = `Total users: ${formatNumber(filteredRows.length)}`;
          if (col.field === "licenseStatus")
            value = `${formatNumber(activePremiumUsersCount)} (${
              filteredRows.length > 0
                ? Math.round(
                    (activePremiumUsersCount / filteredRows.length) * 100,
                  )
                : 0
            }%)`;
          if (col.field === "uniqueExtracts") value = formatNumber(totalNoDocs);
          if (col.field === "limitedExtracts")
            value = formatNumber(totalRequestedPages);
          if (col.field === "generatedExtracts")
            value = formatNumber(totalPages);

          const isRightAligned = [
            "uniqueExtracts",
            "limitedExtracts",
            "generatedExtracts",
          ].includes(col.field);

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.minWidth,
                maxWidth: col.maxWidth,
                display: "flex",
                alignItems: "center",
                justifyContent: isRightAligned ? "flex-end" : "flex-start",
                boxSizing: "border-box",
                fontWeight: "bold",
                color: "#003057",
                p: 1.2,
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
}
